import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styles from "../../../css/ai-mode/chat-window/execution-log-window.module.css";

import ConversationApi from "../../../api/platform/conversationApi";
import { ResponseSuccessModel } from "../../../models/system/responseSuccessModel";
import {
  MessageLogModel,
  StageExecution
} from "../../../models/platform/messageLogModel";

const LONG_TEXT_KEYS = ["prompt", "context", "answer"];
const LONG_TEXT_THRESHOLD = 200;

const CollapsibleText: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const isLong = text.length > LONG_TEXT_THRESHOLD;

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const actionButtons = (marginTop = 0, marginBottom = 0) => (
    <div style={{ display: "flex", gap: 12, marginTop, marginBottom }}>
      {isLong && (
        <button
          onClick={() => setExpanded(e => !e)}
          style={{ fontSize: 11, color: "#3b82f6", background: "none", border: "none", cursor: "pointer", padding: 0 }}
        >
          {expanded ? "▲ Collapse" : "▼ Show full text"}
        </button>
      )}
      <button
        onClick={handleCopy}
        style={{ fontSize: 11, color: copied ? "#15803d" : "#6b7280", background: "none", border: "none", cursor: "pointer", padding: 0 }}
      >
        {copied ? "✓ Copied" : "⎘ Copy"}
      </button>
    </div>
  );

  return (
    <div>
      {actionButtons(0, 4)}
      <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, fontFamily: "Consolas, monospace", fontSize: "12px", color: "#6b7280" }}>
        {isLong && !expanded ? text.slice(0, LONG_TEXT_THRESHOLD) + "..." : text}
      </pre>
      {actionButtons(4, 0)}
    </div>
  );
};

const ExecutionLogPage: React.FC = () => {
  const { assistantMessageId } = useParams();
  const [data, setData] = useState<MessageLogModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsedStages, setCollapsedStages] = useState<Set<string>>(new Set());
  const [copiedAll, setCopiedAll] = useState(false);

  const handleCopyFullLog = () => {
    if (!data) return;
    const logText = JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(logText);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  useEffect(() => {
    const load = async () => {
      if (!assistantMessageId) return;

      const response = await ConversationApi.getMessageLog(
        assistantMessageId
      );

      if (response.success) {
        const result = (response as ResponseSuccessModel)
          .data as MessageLogModel;

        setData(result);
      }

      setLoading(false);
    };

    load();
  }, [assistantMessageId]);

  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (!data) return <div className={styles.loading}>No data found.</div>;

  const totalSeconds = (data.total_time_ms / 1000).toFixed(2);

  const renderDiagnostics = (diagnostics?: Record<string, any>) => {
    if (!diagnostics) return null;

    return (
      <div className={styles.diagnosticsBox}>
        {Object.entries(diagnostics).map(([key, value]) => {
          const isLongKey = LONG_TEXT_KEYS.includes(key);
          const isObject = typeof value === "object" && value !== null;
          const strValue = isObject ? JSON.stringify(value, null, 2) : String(value);

          return (
            <div key={key} className={isObject ? styles.diagnosticRowBlock : styles.diagnosticRow}>
              <span className={styles.diagnosticKey}>{key}</span>
              {isLongKey ? (
                <CollapsibleText text={strValue} />
              ) : isObject ? (
                <pre style={{ margin: 0, fontSize: 12, color: "#6b7280", fontFamily: "Consolas, monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {strValue}
                </pre>
              ) : (
                <span className={styles.diagnosticValue}>{strValue}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const toggleStage = (key: string) => {
    setCollapsedStages(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const renderStage = (stage: StageExecution, level = 0) => {
    const seconds = (stage.duration_ms / 1000).toFixed(2);
    const statusClass = styles[stage.status] || styles.failed;
    const stageKey = `${stage.name}-${stage.order}`;
    const isCollapsed = collapsedStages.has(stageKey);
    const hasContent = stage.error_message || stage.diagnostics || stage.sub_stages?.length;

    return (
      <div
        key={stageKey}
        className={styles.stageCard}
        style={{ marginLeft: level * 24, marginTop: level === 1 ? 8 : 0 }}
      >
        <div className={styles.stageHeader}>
          <div className={styles.stageLeft}>
            <span className={`${styles.statusBadge} ${statusClass}`}>
              {stage.status.toUpperCase()}
            </span>
            <span className={styles.stageName}>{stage.name}</span>
            <span className={styles.stageTime}>{seconds}s</span>
          </div>
          {hasContent && (
            <button
              onClick={() => toggleStage(stageKey)}
              style={{ fontSize: 11, color: "#6b7280", background: "none", border: "none", cursor: "pointer", padding: "0 4px" }}
            >
              {isCollapsed ? "▼" : "▲"}
            </button>
          )}
        </div>

        {!isCollapsed && (
          <>
            {stage.error_message && (
              <div className={styles.errorBox}>
                {stage.error_message}
              </div>
            )}

            {renderDiagnostics(stage.diagnostics)}

            <div style={{ paddingTop: 8 }}>
              {stage.sub_stages?.map(sub =>
                renderStage(sub, level + 1)
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.logPageWrapper}>
      <div className={styles.container}>
        <h1 className={styles.title}>Execution Log</h1>

        {/* OVERALL PERFORMANCE */}
        <div className={styles.overallCard}>
          <div className={styles.overallRow}>
            <span>Total Time</span>
            <strong>{totalSeconds}s</strong>
          </div>

          <div className={styles.overallRow}>
            <span>Overall Status</span>
            <span className={`${styles.statusBadge} ${styles[data.status]}`}>
              {data.status.toUpperCase()}
            </span>
          </div>

          {data.failed_stage && (
            <div className={styles.overallRow}>
              <span>Failed Stage</span>
              <strong>{data.failed_stage}</strong>
            </div>
          )}
        </div>

        <h2 className={styles.stageSectionTitle}>Stages</h2>

        <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
          <button
            onClick={() => setCollapsedStages(new Set(data.stages.map(s => `${s.name}-${s.order}`)))}
            style={{ fontSize: 12, color: "#6b7280", background: "none", border: "1px solid #e5e7eb", borderRadius: 4, cursor: "pointer", padding: "4px 10px" }}
          >
            ▲ Collapse All
          </button>
          <button
            onClick={() => setCollapsedStages(new Set())}
            style={{ fontSize: 12, color: "#6b7280", background: "none", border: "1px solid #e5e7eb", borderRadius: 4, cursor: "pointer", padding: "4px 10px" }}
          >
            ▼ Expand All
          </button>
          <button
            onClick={handleCopyFullLog}
            style={{
              fontSize: 12,
              color: copiedAll ? "#15803d" : "#fff",
              background: copiedAll ? "#dcfce7" : "#6366f1",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              padding: "4px 12px",
              fontWeight: 500,
              marginLeft: "auto",
            }}
          >
            {copiedAll ? "✓ Copied!" : "⎘ Copy Full Log"}
          </button>
        </div>

        {data.stages.map(stage =>
          renderStage(stage)
        )}

        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <button
            onClick={() => setCollapsedStages(new Set(data.stages.map(s => `${s.name}-${s.order}`)))}
            style={{ fontSize: 12, color: "#6b7280", background: "none", border: "1px solid #e5e7eb", borderRadius: 4, cursor: "pointer", padding: "4px 10px" }}
          >
            ▲ Collapse All
          </button>
          <button
            onClick={() => setCollapsedStages(new Set())}
            style={{ fontSize: 12, color: "#6b7280", background: "none", border: "1px solid #e5e7eb", borderRadius: 4, cursor: "pointer", padding: "4px 10px" }}
          >
            ▼ Expand All
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExecutionLogPage;